/**
 * Content Track Progress Utility (Web)
 * Handles loading and initializing content track progress
 */

import { db } from '../firebase';
import { doc, getDoc, collection, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Loads content track progress for a disciple
 * @param {string} trackId - The content track ID
 * @param {string} discipleId - The disciple ID
 * @param {string} mentorAppID - The mentor's app ID
 * @returns {Promise<Object|null>} ContentTrackProgress or null if error
 */
export const loadContentTrackProgress = async (trackId, discipleId, mentorAppID) => {
  try {
    const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', mentorAppID);
    const mentorDetailsDoc = await getDoc(mentorDetailsRef);

    if (!mentorDetailsDoc.exists) {
      return null;
    }

    const existingProgress = mentorDetailsDoc.data()?.contentTrackProgress;
    
    // If progress exists and matches this track, return it
    if (existingProgress && existingProgress.trackId === trackId) {
      // Still refresh the counts in case resources changed
      const resourcesSnapshot = await getDocs(
        collection(db, 'contentTracks', trackId, 'resources')
      );

      const publishedResources = resourcesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(r => r.status === 'published' || !r.status);

      const updatedProgress = {
        ...existingProgress,
        totalPublishedResources: publishedResources.length,
        remainingCount: publishedResources.length - (existingProgress.sentCount || 0),
        completed: publishedResources.length > 0 && (existingProgress.sentCount || 0) >= publishedResources.length,
        lastUpdated: serverTimestamp(),
      };

      return updatedProgress;
    }

    // Calculate initial progress
    const resourcesSnapshot = await getDocs(
      collection(db, 'contentTracks', trackId, 'resources')
    );

    const publishedResources = resourcesSnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(r => r.status === 'published' || !r.status);

    const initialProgress = {
      trackId,
      totalPublishedResources: publishedResources.length,
      sentResourceIds: [],
      sentCount: 0,
      remainingCount: publishedResources.length,
      completed: false,
      lastUpdated: serverTimestamp(),
    };

    return initialProgress;
  } catch (error) {
    console.error('Error loading content track progress:', error);
    return null;
  }
};

/**
 * Initializes or refreshes content track progress when a track is subscribed
 * @param {string} trackId - The content track ID
 * @param {string} discipleId - The disciple ID
 * @param {string} mentorAppID - The mentor's app ID
 * @returns {Promise<Object|null>} ContentTrackProgress or null if error
 */
export const initializeContentTrackProgress = async (trackId, discipleId, mentorAppID) => {
  return loadContentTrackProgress(trackId, discipleId, mentorAppID);
};




